import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { AnalysisActionType } from '@modules/analysis/types';
import { CanAccess } from '@modules/common/components/CanAccess';
import { CopyArtefactLinkButton } from '@modules/common/components/CopyArtefactLinkButton';
import { ArtefactType } from '@modules/common/types';
import { AddAnalysisToDashboardButton } from '@modules/dashboard/components';
import { trackSearchV2Events } from '@modules/feed/components/BasicSearchDropdown/BasicSearchDropdownV2';
import { ResetSearchAction } from '@modules/feed/components/ResetSearchAction';
import { SaveSearchActions } from '@modules/feed/components/SearchAnalysisToolbar/Actions';
import { isSavedSearch } from '@modules/feed/components/SearchAnalysisToolbar/utils';
import { SearchSubscribeFeedbackV2 } from '@modules/feed/components/SearchSubscribeFeedback';
import { useSaveFeedAnalysis } from '@modules/feed/hooks';
import { Box } from '@mui/material';
import {
  Scope as EScope,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useMemo } from 'react';
import { SaveFeedAndAddToDashboardDialog } from '../../SaveFeedAndAddToDashboardDialog';
import { SaveSearchAction } from '../Actions/SaveSearchAction';

export const useGetSearchRightActions = () => {
  const { onSaveSearchDashboard } = useSaveFeedAnalysis();
  const id = useAnalysisStore(({ id }) => id);
  const isSystemGenerated = useAnalysisStore(
    ({ scope }) => scope === EScope.SystemGenerated,
  );

  const onCopyClick = (link: string) =>
    trackSearchV2Events({
      eventName: 'Search share link copied',
      url: link,
    });

  const rightActions = useMemo(
    () =>
      isSystemGenerated
        ? [
            {
              component: (
                <Box>
                  <CopyArtefactLinkButton
                    artefactType={ArtefactType.Search}
                    artefactLink={`${window.location.origin}${window.location.pathname}`}
                    onClick={onCopyClick}
                    onSave={onSaveSearchDashboard}
                    isSavedArtefact={isSavedSearch(id)}
                  />
                </Box>
              ),
              key: 'Copy Search Link',
            },
            {
              component: (
                <Box>
                  <SaveSearchAction actionType={AnalysisActionType.SAVE_AS} />
                </Box>
              ),
              key: 'Save Search',
            },
          ]
        : [
            {
              component: <ResetSearchAction />,
              key: 'Reset Search',
            },
            {
              component: (
                <Box>
                  <CopyArtefactLinkButton
                    artefactType={ArtefactType.Search}
                    artefactLink={`${window.location.origin}${window.location.pathname}`}
                    onClick={onCopyClick}
                    onSave={onSaveSearchDashboard}
                    isSavedArtefact={isSavedSearch(id)}
                  />
                </Box>
              ),
              key: 'Copy Search Link',
            },
            {
              component: (
                <CanAccess
                  resource={PermissionResource.Artefact}
                  operation={PermissionOperation.Write}
                >
                  <AddAnalysisToDashboardButton
                    isDisabled={false}
                    renderAnalysisFormDialog={(params) => {
                      return <SaveFeedAndAddToDashboardDialog {...params} />;
                    }}
                  />
                </CanAccess>
              ),
              key: 'Add Feed to Dashboard',
            },
            {
              component: <SaveSearchActions />,
              key: 'Save Search',
            },
            {
              component: <SearchSubscribeFeedbackV2 />,
              key: 'Subscribe Feedback',
            },
          ],
    [isSystemGenerated, onSaveSearchDashboard, id],
  );

  return rightActions;
};
